import { supabase } from '@/integrations/supabase/client';
import type { TimeSlot as AvailabilityTimeSlot } from '@/hooks/useAvailability'; // Import TimeSlot type

// Exporter l'interface TimeSlot
export interface TimeSlot {
  time: string;
  isAvailable: boolean;
}

export const sendBookingRequest = async (
  senderId: string,
  recipientId: string,
  appointmentDate: Date,
  timeSlot: string,
  duration: number = 60
) => {
  if (!recipientId || !senderId) return null;
  
  const formattedDate = appointmentDate.toLocaleDateString('fr-FR', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
  
  const appointmentMessage = `Demande de rendez-vous pour le ${formattedDate} à ${timeSlot} (durée: ${duration} minutes)`;
  
  // Use the message service to send a message marked as a booking request
  return await supabase
    .from('messages')
    .insert({
      content: appointmentMessage,
      sender_id: senderId,
      recipient_id: recipientId,
      is_read: false,
      is_booking_request: true,
      content_type: 'text'
    })
    .select()
    .single();
};

// New function to handle the RPC call for creating a booking record
export const createBooking = async (
  clientId: string,
  escortId: string,
  bookingDateTime: Date,
  duration: string | number, // Can be string like '60' or number 60
  price: number,
  currency: string,
  status: string = 'pending',
  notes: string = ''
): Promise<{ booking_id: string }> => { // Return non-nullable on success, throw otherwise
  console.log(`Service: Attempting to create booking for client ${clientId} with escort ${escortId}`);
  
  // Convert duration to string for RPC if it's a number (RPC expects TEXT)
  const p_duration_text = typeof duration === 'number' ? duration.toString() : duration;
  
  const { data, error } = await supabase.rpc(
    'create_booking',
    {
      p_client_id: clientId,
      p_escort_id: escortId,
      p_booking_date: bookingDateTime.toISOString(),
      p_duration: p_duration_text, // Pass as TEXT
      p_price: price,
      p_currency: currency, // Assuming TEXT is fine
      p_status: status,     // Assuming TEXT is fine
      p_notes: notes
    }
  );

  if (error) {
    console.error('Service Error: RPC call failed for create_booking:', error);
    // You might want to map specific PostgreSQL errors to user-friendly messages here
    throw new Error('Failed to create booking due to a database error.'); // Throw a generic error
  }

  // Check the response structure from the RPC function
  if (data && typeof data === 'object') {
    const response = data as { success: boolean; booking_id?: string; message?: string };
    
    if (response.success === true && response.booking_id) {
      console.log('Service: Booking created successfully with ID:', response.booking_id);
      return { booking_id: response.booking_id }; // Return the booking ID on success
    } else {
      // Booking failed (e.g., slot conflict or other handled error in RPC)
      const failureMessage = response.message || 'Booking creation failed. The time slot might be unavailable.';
      console.warn('Service: Booking creation denied by RPC:', failureMessage);
      throw new Error(failureMessage); // Throw error with the message from RPC
    }
  } else {
    // Unexpected response from RPC
    console.error('Service Error: Unexpected response structure from create_booking RPC:', data);
    throw new Error('An unexpected error occurred during booking creation.');
  }
};

export const getAvailableTimeSlots = async (
  escortId: string | undefined,
  date: Date,
  duration: number = 60
): Promise<TimeSlot[]> => {
  if (!escortId || !date) return [];
  
  try {
    const formattedDate = date.toISOString().split('T')[0];
    
    console.log(`Service: Fetching slots for ${escortId} on ${formattedDate} with duration ${duration}`);
    
    const { data, error } = await supabase.rpc(
      'get_available_time_slots',
      {
        p_escort_id: escortId,
        p_date: formattedDate,
        p_duration: duration
      }
    );
    
    if (error) {
      console.error('Service Error: getting available time slots:', error);
      throw error;
    }
    
    // Assuming RPC returns an array of objects like [{ time_slot: 'HH:MM' }, ... ]
    // Or potentially just an array of strings: ['HH:MM', ... ]
    // Adjust the mapping based on the actual RPC return structure.
    
    // Map the result to TimeSlot[]
    let timeStrings: string[] = [];
    if (Array.isArray(data)) {
      if (data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'time_slot' in data[0]) {
        // Case: [{ time_slot: 'HH:MM' }, ... ]
        timeStrings = data.map((slot: { time_slot: string }) => slot.time_slot);
      } else if (data.length > 0 && typeof data[0] === 'string'){
         // Case: ['HH:MM', ... ]
         timeStrings = data;
      }
    }

    const slots: TimeSlot[] = timeStrings.map(time => ({
      time: time,
      isAvailable: true // Assume all returned slots are available
    }));

    console.log('Service: Available time slots processed:', slots);
    
    return slots;
  } catch (err) {
    // Log the error but return empty array to the component
    console.error('Service Error caught in getAvailableTimeSlots:', err);
    return [];
  }
};
